import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faRefresh,
  faSearch,
  faFilter,
  faTruck,
  faGasPump,
  faBatteryFull,
  faWrench,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faEye,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faCalendarAlt,
  faUser,
  faNotesMedical,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import { API_URL } from "../components/config";

const RoadAssistanceManagement = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch all road assistance requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/road-assistance/requests`);
      const result = await response.json();
      
      if (result.status === '200') {
        setRequests(result.data || []);
      } else {
        console.error('Failed to fetch requests:', result.message);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Update request status
  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      setIsUpdating(true);
      const response = await fetch(`${API_URL}/road-assistance/request/${requestId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const result = await response.json();
      
      if (result.status === '200') {
        // Refresh requests
        fetchRequests();
        setSelectedRequest(null);
      } else {
        console.error('Failed to update status:', result.message);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: faClock, label: 'Pending' },
      dispatched: { color: 'info', icon: faTruck, label: 'Dispatched' },
      completed: { color: 'success', icon: faCheckCircle, label: 'Completed' },
      cancelled: { color: 'danger', icon: faTimesCircle, label: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`badge bg-${config.color} text-white`}>
        <FontAwesomeIcon icon={config.icon} className="me-1" />
        {config.label}
      </span>
    );
  };

  // Get assistance type icon
  const getAssistanceIcon = (type) => {
    const icons = {
      towing: faTruck,
      petrol: faGasPump,
      diesel: faGasPump,
      battery: faBatteryFull,
      tyre: faWrench,
    };
    return icons[type] || faWrench;
  };

  // Filter requests
  const filteredRequests = requests.filter(request => {
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      request.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.user_phone?.includes(searchTerm) ||
      request.assistance_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container-fluid"
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h3 mb-0">Road Assistance Management</h1>
          <button 
            className="btn btn-primary"
            onClick={fetchRequests} 
            disabled={loading}
          >
            <FontAwesomeIcon 
              icon={faRefresh} 
              className={`me-2 ${loading ? 'fa-spin' : ''}`} 
            />
            Refresh
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">Total Requests</p>
                    <h4 className="mb-0">{requests.length}</h4>
                  </div>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-muted fa-2x" />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card bg-warning bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">Pending</p>
                    <h4 className="mb-0 text-warning">
                      {requests.filter(r => r.status === 'pending').length}
                    </h4>
                  </div>
                  <FontAwesomeIcon icon={faClock} className="text-warning fa-2x" />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card bg-info bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">Dispatched</p>
                    <h4 className="mb-0 text-info">
                      {requests.filter(r => r.status === 'dispatched').length}
                    </h4>
                  </div>
                  <FontAwesomeIcon icon={faTruck} className="text-info fa-2x" />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card bg-success bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1">Completed</p>
                    <h4 className="mb-0 text-success">
                      {requests.filter(r => r.status === 'completed').length}
                    </h4>
                  </div>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success fa-2x" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <select 
                  className="form-select"
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Requests Table */}
        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <FontAwesomeIcon icon={faRefresh} className="fa-spin fa-2x" />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Customer</th>
                      <th>Type</th>
                      <th>Notes</th>
                      <th>Status</th>
                      <th>Request Time</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((request) => {
                      const Icon = getAssistanceIcon(request.assistance_type);
                      return (
                        <tr key={request.id} className="cursor-pointer">
                          <td className="fw-bold">#{request.id}</td>
                          <td>
                            <div>
                              <div className="fw-medium">{request.user_name}</div>
                              <small className="text-muted">{request.user_phone}</small>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon={Icon} className="text-primary" />
                              <span className="text-capitalize">{request.assistance_type}</span>
                            </div>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={request.notes}>
                              {request.notes || 'No notes'}
                            </div>
                          </td>
                          <td>{getStatusBadge(request.status)}</td>
                          <td><small>{formatDate(request.request_datetime)}</small></td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setSelectedRequest(request)}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-1" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Request Details Modal */}
        {selectedRequest && (
          <div 
            className="modal fade show" 
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Road Assistance Request #{selectedRequest.id}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSelectedRequest(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  {/* Customer Information */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Customer Information</h6>
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <small className="text-muted">Name</small>
                        <p className="mb-0 fw-medium">{selectedRequest.user_name}</p>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted">Phone</small>
                        <p className="mb-0 fw-medium">
                          <FontAwesomeIcon icon={faPhone} className="me-2" />
                          {selectedRequest.user_phone}
                        </p>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted">Email</small>
                        <p className="mb-0 fw-medium">
                          <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                          {selectedRequest.user_email || 'Not provided'}
                        </p>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted">User ID</small>
                        <p className="mb-0 fw-medium">
                          <FontAwesomeIcon icon={faUser} className="me-2" />
                          {selectedRequest.user_id}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Request Details */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Request Details</h6>
                    <div className="mb-3">
                      <small className="text-muted">Assistance Type</small>
                      <p className="mb-0">
                        <FontAwesomeIcon 
                          icon={getAssistanceIcon(selectedRequest.assistance_type)} 
                          className="me-2 text-primary" 
                        />
                        <span className="fw-medium text-capitalize">
                          {selectedRequest.assistance_type}
                        </span>
                      </p>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Notes</small>
                      <div className="p-3 bg-light rounded">
                        <FontAwesomeIcon icon={faNotesMedical} className="me-2 text-muted" />
                        {selectedRequest.notes || 'No notes provided'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Location</small>
                      <p className="mb-0">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" />
                        {selectedRequest.latitude && selectedRequest.longitude
                          ? `${selectedRequest.latitude}, ${selectedRequest.longitude}`
                          : 'Location not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Status Management */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Status Management</h6>
                    <div className="mb-3">
                      <small className="text-muted">Current Status</small>
                      <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                    </div>
                    <div className="d-flex gap-2">
                      {selectedRequest.status === 'pending' && (
                        <button
                          onClick={() => updateRequestStatus(selectedRequest.id, 'dispatched')}
                          disabled={isUpdating}
                          className="btn btn-info"
                        >
                          <FontAwesomeIcon icon={faTruck} className="me-2" />
                          Mark as Dispatched
                        </button>
                      )}
                      {selectedRequest.status === 'dispatched' && (
                        <button
                          onClick={() => updateRequestStatus(selectedRequest.id, 'completed')}
                          disabled={isUpdating}
                          className="btn btn-success"
                        >
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          Mark as Completed
                        </button>
                      )}
                      {(selectedRequest.status === 'pending' || selectedRequest.status === 'dispatched') && (
                        <button
                          onClick={() => updateRequestStatus(selectedRequest.id, 'cancelled')}
                          disabled={isUpdating}
                          className="btn btn-danger"
                        >
                          <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                          Cancel Request
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div>
                    <h6 className="fw-bold mb-3">Timestamps</h6>
                    <div className="row">
                      <div className="col-md-4 mb-2">
                        <small className="text-muted">Request Time</small>
                        <p className="mb-0">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                          {formatDate(selectedRequest.request_datetime)}
                        </p>
                      </div>
                      {selectedRequest.dispatched_datetime && (
                        <div className="col-md-4 mb-2">
                          <small className="text-muted">Dispatched Time</small>
                          <p className="mb-0">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {formatDate(selectedRequest.dispatched_datetime)}
                          </p>
                        </div>
                      )}
                      {selectedRequest.completed_datetime && (
                        <div className="col-md-4 mb-2">
                          <small className="text-muted">Completed Time</small>
                          <p className="mb-0">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {formatDate(selectedRequest.completed_datetime)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setSelectedRequest(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </DashboardLayout>
  );
};

export default RoadAssistanceManagement;
